// A deliberately small history-based router. Workspace scope lives in the
// session (a selected workspace), so app routes stay flat: /tracker, /reports…
// (The DESIGN names TanStack Router for URL-encoded report filters; this keeps
// the app shippable and swappable later.)

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface RouterValue {
  path: string;
  navigate: (to: string, opts?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string, opts?: { replace?: boolean }) => {
    if (to === window.location.pathname) return;
    if (opts?.replace) window.history.replaceState(null, "", to);
    else window.history.pushState(null, "", to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext);
  if (!ctx) throw new Error("useRouter must be used within RouterProvider");
  return ctx;
}

interface LinkProps {
  to: string;
  className?: string;
  children: ReactNode;
  replace?: boolean;
  title?: string;
}

export function Link({ to, className, children, replace, title }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      title={title}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to, { replace });
      }}
    >
      {children}
    </a>
  );
}

/** matchPrefix returns the remainder after a "/prefix/" match, or null. */
export function matchPrefix(path: string, prefix: string): string | null {
  const full = `/${prefix}/`;
  if (path.startsWith(full)) return decodeURIComponent(path.slice(full.length));
  return null;
}
